/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { connect, useSelector } from 'react-redux';
import { IntlProvider } from 'react-intl';
import * as _ from 'lodash';

import { selectors as listLocaleSelector, actions as listLocaleActions } from '../redux/ducks/lists/listLocale';
import { selectors as localeSelector, actions as localeActions } from '../redux/ducks/locale';
import { selectors as userSelector } from '../redux/ducks/user';
import WindowTitle from './WindowTitle';
import { useActiveUser } from '../hooks/useUser';
import { useLists } from '../hooks/useLists';

function mapStateToProps(state) {
  const defaultLocaleId = listLocaleSelector.getClosestMatchingLocaleId(state, localeSelector.getClientLocales(state));
  const userLocaleId = userSelector.getLocaleId(state);
  const localeId = userLocaleId > 0 ? userLocaleId : defaultLocaleId;
  const localeData = listLocaleSelector.getLocale(localeId, state);

  return {
    localeData,
    localeList: listLocaleSelector.getLocaleCollection(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    setLocale: (localeData) => {
      dispatch(localeActions.setCurrentLocale(localeData));
    },
  };
}

const ConnectedIntlProvider = (props) => {
  const { setLocale, children } = props;
  const [{ preferredLanguageCode }, userActions] = useActiveUser();
  const messages = useSelector(state => localeSelector.getMessages(state, preferredLanguageCode));
  const messageLength = Object.keys(messages).length;
  const [lastLanguageCode, setLastLanguageCode] = React.useState(preferredLanguageCode);
  const [{ isLoaded, localeList }, listActions] = useLists();

  React.useEffect(() => {
    if (!preferredLanguageCode || !isLoaded) { return; }
    const localeData = localeList.find(l => l.language_code === preferredLanguageCode) || localeList[0];
    if (lastLanguageCode !== preferredLanguageCode) {
      setLocale(localeData);
      setLastLanguageCode(preferredLanguageCode);
    }
    // React-Intl stores locale in the React context. Thus, there's no easy way
    // for descendant components to get notified of locale change.
    // One possible way would be to pass locale as key to IntlProvider,
    // but that will make component tree re-mount on each locale change with loosing any component state.
    // Instead, here we force component tree to repaint itself, which is a possible way to handle this particular scenario,
    // see https://github.com/facebook/react/issues/2517
    // if (childRef.current) {
    //   deepForceUpdate(childRef.current);
    // }
  }, [preferredLanguageCode, isLoaded, messageLength]);

  React.useEffect(() => {
    if (!isLoaded) { return; }
    const l = listActions.getClosestMatchingLocale();
    userActions.setClientLocale(l);
  }, [isLoaded]);

  if (!preferredLanguageCode || !isLoaded) { return null; }

  return (
    <IntlProvider
      locale={preferredLanguageCode}
      messages={messages}
      defaultLocale="en"
    >
      <WindowTitle>
        {children}
      </WindowTitle>
    </IntlProvider>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(ConnectedIntlProvider);
